import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRoute,
} from "@tanstack/react-router"
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools"
import { TanStackDevtools } from "@tanstack/react-devtools"
import { RotateCcw, Search } from "lucide-react"

import { AppShell } from "@/components/app-shell"
import { Button } from "@/components/ui/button"
import { Toaster } from "@/components/ui/sonner"
import { TooltipProvider } from "@/components/ui/tooltip"
import { getViewerFn } from "@/lib/app.functions"
import { APP_NAME } from "@/lib/constants"
import { LocaleProvider } from "@/lib/i18n"
import appCss from "../styles.css?url"
import type { ErrorComponentProps } from "@tanstack/react-router"

export const Route = createRootRoute({
  loader: () => getViewerFn(),
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: `${APP_NAME} · Plan a smarter property move`,
      },
      {
        name: "description",
        content:
          "Explore fictional homes, rentals, land, and commercial property in a complete real-estate marketplace demo.",
      },
      { name: "application-name", content: APP_NAME },
      { name: "theme-color", content: "#101c31" },
      { name: "robots", content: "index, follow" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: APP_NAME },
      {
        property: "og:title",
        content: `${APP_NAME} · Plan a smarter property move`,
      },
      {
        property: "og:description",
        content:
          "Search, compare, save, inquire, and manage fictional listings in one complete real-estate marketplace demo.",
      },
      {
        property: "og:image",
        content: "https://realestate.jericop.dev/brand/harbor-pine-social.png",
      },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: "Harbor & Pine Realty property marketplace demo",
      },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: `${APP_NAME} · Plan a smarter property move`,
      },
      {
        name: "twitter:description",
        content:
          "A complete fictional real-estate marketplace for discovery, saved work, and listing operations.",
      },
      {
        name: "twitter:image",
        content: "https://realestate.jericop.dev/brand/harbor-pine-social.png",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "icon",
        href: "/brand/harbor-pine-mark.svg",
        type: "image/svg+xml",
      },
      {
        rel: "icon",
        href: "/favicon.ico",
        sizes: "64x64 48x48 32x32 16x16",
      },
      {
        rel: "apple-touch-icon",
        href: "/brand/apple-touch-icon.png",
        sizes: "180x180",
      },
      { rel: "manifest", href: "/manifest.json" },
    ],
  }),
  component: RootComponent,
  errorComponent: RootError,
  notFoundComponent: NotFound,
  shellComponent: RootDocument,
})

function RootError({ reset }: ErrorComponentProps) {
  return (
    <main className="grid min-h-svh place-items-center bg-background p-6">
      <section className="field-panel w-full max-w-2xl bg-white p-7 sm:p-10">
        <div className="flex items-center gap-4 border-b pb-6">
          <img
            src="/brand/harbor-pine-mark.svg"
            alt=""
            width={64}
            height={64}
            className="size-14 border border-foreground"
            aria-hidden="true"
          />
          <div>
            <p className="font-heading text-2xl leading-none font-bold uppercase">
              Harbor <span className="text-primary">&</span> Pine
            </p>
            <p className="mt-1 font-heading text-[10px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
              Realty · Demo environment
            </p>
          </div>
        </div>
        <p className="field-label mt-8">Temporary interruption</p>
        <h1 className="field-heading mt-3">This page needs another try.</h1>
        <p className="mt-5 max-w-xl leading-7 text-muted-foreground">
          The page could not finish loading. Retry the current view, or return
          to the property catalogue and continue browsing.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button className="field-button" onClick={reset}>
            <RotateCcw />
            Try again
          </Button>
          <Button
            variant="outline"
            className="field-button"
            nativeButton={false}
            render={<a href="/search" />}
          >
            <Search />
            Browse properties
          </Button>
        </div>
        <p className="mt-8 border-t pt-4 text-xs text-muted-foreground">
          Demo environment · No real listings or transactions
        </p>
      </section>
    </main>
  )
}

function RootComponent() {
  const viewer = Route.useLoaderData()
  return (
    <LocaleProvider initialLocale={viewer?.locale}>
      <TooltipProvider>
        <AppShell user={viewer}>
          <Outlet />
        </AppShell>
        <Toaster position="top-right" richColors />
      </TooltipProvider>
    </LocaleProvider>
  )
}

function NotFound() {
  return (
    <main className="field-container py-24">
      <p className="field-label">Page not found · 404</p>
      <h1 className="field-heading mt-3">We could not find that page.</h1>
      <p className="mt-6 max-w-xl text-muted-foreground">
        The page may have moved, or the address may be incomplete.
      </p>
      <a href="/" className="field-link mt-8 inline-block">
        Return to property search
      </a>
    </main>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        {import.meta.env.DEV && import.meta.env.MODE !== "browser-test" ? (
          <TanStackDevtools
            config={{ position: "bottom-right" }}
            plugins={[
              {
                name: "TanStack Router",
                render: <TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        ) : null}
        <Scripts />
      </body>
    </html>
  )
}
